import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Le panneau de l'application : une carte bordée et son bandeau de titre.
 *
 * Il vivait dans le tableau de bord, et cinq pages avaient écrit le leur —
 * titre en capitales condensées ici, en minuscules grasses là, tantôt dans la
 * carte, tantôt au-dessus, une bordure sur l'une et pas sur sa voisine. Une
 * section se reconnaît désormais au même bandeau partout.
 *
 * Le bandeau a une hauteur fixe : avec ou sans bouton, deux panneaux voisins
 * ouvrent leur contenu à la même ligne.
 */
export function Panel({
  title,
  icon: Icon,
  aside,
  actions,
  className,
  children,
}: {
  title: ReactNode;
  icon?: LucideIcon;
  /** Un repère discret à droite du titre : un nombre, un total, une unité. */
  aside?: ReactNode;
  /** Les gestes du panneau, à droite. Des boutons `size="sm"`. */
  actions?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={cn("bg-bg-card border border-border min-w-0", className)}>
      {/* Titre, repère et gestes sont trois éléments d'une même rangée qui
          passe à la ligne : sur un téléphone, le repère descend sous le titre
          au lieu d'être écrasé contre le bouton. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 min-h-14 px-5 py-2 border-b border-border">
        <h2 className="flex items-center gap-2 min-w-0 mr-auto font-serif text-[15px] font-semibold uppercase tracking-[0.08em] text-text-primary">
          {Icon && <Icon size={15} aria-hidden className="text-accent-ink shrink-0" />}
          <span className="min-w-0">{title}</span>
        </h2>
        {aside != null && <span className="ml-auto text-xs text-text-muted">{aside}</span>}
        {actions != null && <div className="ml-auto flex items-center gap-2 shrink-0">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

/** Le corps d'un panneau qui n'est pas une liste : la marge intérieure commune. */
export function PanelBody({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn("px-5 py-4", className)}>{children}</div>;
}

/** Ce qu'affiche un panneau vide : une phrase, centrée, discrète. */
export function PanelEmpty({ children }: { children: ReactNode }) {
  return <p className="text-xs text-text-muted text-center px-5 py-8">{children}</p>;
}
